// src/components/ClientCard.js
import React from 'react';
import {
  PHASES, PHASE_COLORS, LP_STEPS, TRACK_STEPS, PM_STEPS,
  PLAT_DELIVERABLES, LP_PLATFORMS, LP_DELIVERABLES, resolveLabel, platShort, platColor,
} from '../constants';

function daysSince(c) {
  if (!c.lastChecked) return 999;
  return Math.floor((new Date() - new Date(c.lastChecked)) / 86400000);
}

function alertLevel(c) {
  if (c.onHold || c.phase === 'postlaunch' || c.phase === 'cancelled') return '';
  const d = daysSince(c);
  if (d >= 7) return 'urgent';
  if (d >= 4) return 'stale';
  return '';
}

function missingDelivCount(c) {
  let miss = 0;
  (c.platforms || []).forEach(p => {
    const aType = p.accountType || 'new';
    const dKey = (p.name === 'LSA' && aType === 'new') ? 'LSA_new' : p.name;
    const masterList = (PLAT_DELIVERABLES[dKey] || []).map(d => resolveLabel(d, aType));
    masterList.forEach(d => { if (!p.deliverables?.[d]) miss++; });
    if (LP_PLATFORMS.includes(p.name) && p.lpDelivEnabled !== false) {
      // count LP deliverables
      const LP_DELIVERABLES = require('../constants').LP_DELIVERABLES;
      LP_DELIVERABLES.forEach(d => { if (!p.lpDeliverables?.[d]) miss++; });
    }
  });
  return miss;
}


function MiniBar({ label, done, total, color, doneColor }) {
  const pct = Math.round((done / total) * 100);
  const allDone = done === total;
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: allDone ? doneColor : color, marginBottom: 2, fontWeight: 600 }}>
        <span>{label}: {allDone ? '✓ Complete' : (label === 'LP' ? LP_STEPS[done] || 'Done' : `${done}/${total}`)}</span>
        <span>{done}/{total}</span>
      </div>
      <div style={{ height: 4, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 3,
          background: allDone ? doneColor : color,
          width: `${pct}%`,
          transition: 'width 0.3s',
        }} />
      </div>
    </div>
  );
}

export default function ClientCard({ client, onClick, onToggleImportant }) {
  const c = client;
  const al = alertLevel(c);
  const days = daysSince(c);
  const totalBudget = (c.platforms || []).reduce((s, p) => s + (parseFloat(p.budget) || 0), 0);
  const missing = missingDelivCount(c);
  const trackDone = TRACK_STEPS.filter(s => c.trackSteps?.[s]).length;
  const lpDone = LP_STEPS.filter(s => c.lpSteps?.[s]).length;
  const pmDone = PM_STEPS.filter(s => c.pmSteps?.[s]).length;
  const hasLP = !!c.lpEnabled && (c.platforms || []).some(p => LP_PLATFORMS.includes(p.name));

  const noAccessPlats = (c.platforms || []).filter(p => p.accountType === 'existing' && !p.accessGranted);

  // Card border
  let borderStyle = '1px solid rgba(0,0,0,0.1)';
  if (c.important) borderStyle = '2px solid #185FA5';
  let borderTop = al === 'urgent' ? '3px solid #E24B4A' : al === 'stale' ? '3px solid #EF9F27' : undefined;

  const isCancelled = c.phase === 'cancelled';

  return (
    <div
      onClick={onClick}
      style={{
        background: isCancelled ? '#f7f7f5' : '#fff',
        border: isCancelled ? '1px solid rgba(0,0,0,0.12)' : borderStyle,
        borderTop: isCancelled ? undefined : borderTop,
        borderRadius: 8,
        padding: '8px 10px',
        cursor: 'pointer',
        opacity: isCancelled ? 0.55 : c.onHold ? 0.65 : 1,
        borderLeft: isCancelled ? '3px solid #999' : c.onHold ? '3px solid #E24B4A' : (c.important ? '2px solid #185FA5' : undefined),
        filter: isCancelled ? 'grayscale(0.4)' : undefined,
        transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
    >
      {/* Name row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, marginBottom: 2 }}>
        <div style={{
          fontSize: 12, fontWeight: 800, flex: 1,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          color: '#1a1a18',
        }} title={c.name}>
          {c.name}
        </div>
        <button
          onClick={e => { e.stopPropagation(); onToggleImportant(c.id); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 15, padding: '0 1px', lineHeight: 1,
            color: c.important ? '#185FA5' : '#ccc',
            opacity: c.important ? 1 : 0.5,
          }}
          title="Focus Next"
        >★</button>
      </div>

      {/* CID */}
      {c.cids && (
        <div style={{
          fontSize: 10, color: '#888', fontFamily: 'monospace',
          marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {c.cids.length > 32 ? c.cids.slice(0, 32) + '…' : c.cids}
        </div>
      )}

      {/* Alert */}
      {al === 'urgent' && (
        <div style={{
          fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
          marginBottom: 4, display: 'inline-block',
          background: '#FCEBEB', color: '#A32D2D',
        }}>
          ⚠ {days} days since check — URGENT
        </div>
      )}
      {al === 'stale' && (
        <div style={{
          fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
          marginBottom: 4, display: 'inline-block',
          background: '#FAEEDA', color: '#854F0B',
        }}>
          ▲ {days} days since check
        </div>
      )}

      {/* PM notes snippet */}
      {c.opspm && c.pmNotes && (
        <div style={{
          fontSize: 11, color: '#27500A',
          background: '#EAF3DE', border: '1px solid #b8dfa0',
          borderRadius: 4, padding: '3px 7px', marginBottom: 4,
        }}>
          📋 {c.pmNotes.length > 80 ? c.pmNotes.slice(0, 80) + '…' : c.pmNotes}
        </div>
      )}
      {/* Pinned note */}
      {c.pinNote && (
        <div style={{
          fontSize: 11, fontWeight: 600, color: '#854F0B',
          background: '#FAEEDA', border: '1px solid #f0d090',
          borderRadius: 4, padding: '3px 7px', marginBottom: 4,
        }}>
          📌 {c.pinNote}
        </div>
      )}

      {/* Platform tags */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 4 }}>
        {(c.platforms || []).map((p, i) => {
          const col = platColor(p.name);
          return (
            <span key={i} style={{
              fontSize: 10, padding: '1px 5px', borderRadius: 3,
              fontWeight: 700, background: col.bg, color: col.text,
            }}>
              {platShort(p.name)}
            </span>
          );
        })}
        {c.onHold && (
          <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, fontWeight: 700, background: '#FCEBEB', color: '#A32D2D' }}>Hold</span>
        )}
        {isCancelled && (
          <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, fontWeight: 700, background: '#f0f0ee', color: '#666' }}>✕ Cancelled</span>
        )}
        {c.important && (
          <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, fontWeight: 700, background: '#E6F1FB', color: '#0C447C' }}>★ Focus</span>
        )}
        {c.opspm && (
          <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, fontWeight: 700, background: '#EAF3DE', color: '#27500A' }}>📋 Ops PM</span>
        )}
      </div>

      {/* No access warning */}
      {noAccessPlats.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 4 }}>
          {noAccessPlats.map((p, i) => (
            <span key={i} style={{ fontSize: 10, fontWeight: 700, color: '#A32D2D', background: '#FCEBEB', borderRadius: 3, padding: '1px 5px' }}>
              {platShort(p.name)}: No Access
            </span>
          ))}
        </div>
      )}

      {/* Platform status badges with launch dates */}
      {(c.platforms || []).some(p => p.built || p.posted || p.launched || p.live) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 4 }}>
          {(c.platforms || []).map((p, i) => {
            const badges = [];
            if (p.built) badges.push({ label: `${platShort(p.name)}: Built`, bg: '#E6F1FB', color: '#185FA5' });
            if (p.posted) badges.push({ label: `${platShort(p.name)}: Posted`, bg: '#EAF3DE', color: '#27500A' });
            if (p.launched) {
              const lbl = p.launchDate
                ? `🚀 ${platShort(p.name)} Launched ${p.launchDate}`
                : `🚀 ${platShort(p.name)}: Launched`;
              badges.push({ label: lbl, bg: '#dcf0e8', color: '#0F6E56' });
            }
            if (p.live && !p.launched) badges.push({ label: `${platShort(p.name)}: Live`, bg: '#dcf0e8', color: '#0F6E56' });
            return badges.map((b, j) => (
              <span key={`${i}-${j}`} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, fontWeight: 700, background: b.bg, color: b.color }}>
                {b.label}
              </span>
            ));
          })}
        </div>
      )}

      {/* Flags: deliverables + tracking */}
      {(missing > 0 || trackDone < TRACK_STEPS.length) && (
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginBottom: 4 }}>
          {missing > 0 && (
            <span style={{ fontSize: 10, padding: '2px 5px', borderRadius: 3, fontWeight: 700, background: '#FCEBEB', color: '#A32D2D' }}>
              {missing} deliverable{missing > 1 ? 's' : ''} needed
            </span>
          )}
          {trackDone < TRACK_STEPS.length && (
            <span style={{ fontSize: 10, padding: '2px 5px', borderRadius: 3, fontWeight: 700, background: '#E6F1FB', color: '#185FA5' }}>
              Trk {trackDone}/{TRACK_STEPS.length}
            </span>
          )}
        </div>
      )}

      {/* Process mini progress bars */}
      {hasLP && (
        <MiniBar label="LP" done={lpDone} total={LP_STEPS.length} color="#BA7517" doneColor="#3B6D11" />
      )}
      {trackDone > 0 && trackDone < TRACK_STEPS.length && (
        <MiniBar label="Trk" done={trackDone} total={TRACK_STEPS.length} color="#1D9E75" doneColor="#0F6E56" />
      )}
      {trackDone === TRACK_STEPS.length && (
        <MiniBar label="Trk" done={trackDone} total={TRACK_STEPS.length} color="#0F6E56" doneColor="#0F6E56" />
      )}
      {pmDone > 0 && (
        <MiniBar label="PM" done={pmDone} total={PM_STEPS.length} color="#185FA5" doneColor="#0C447C" />
      )}

      {/* Bottom: CSM/PM + date */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <span style={{ fontSize: 10, color: '#888', flex: 1 }}>
          {[c.csm && `CSM: ${c.csm}`, c.pm && `PM: ${c.pm}`, c.strategist].filter(Boolean).join(' / ') || '—'}
        </span>
        <span style={{ fontSize: 10, color: '#888' }}>{c.lastChecked || 'never'}</span>
      </div>

      {/* Budget + MRR */}
      <div style={{ display: 'flex', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
        {totalBudget > 0 && (
          <div style={{ fontSize: 10, color: '#888' }}>
            Spend: ${totalBudget.toLocaleString()}/mo
          </div>
        )}
        {c.mrrAmount > 0 && (
          <div style={{ fontSize: 10, fontWeight: 700, color: '#185FA5' }}>
            MRR: ${Number(c.mrrAmount).toLocaleString()}/mo
          </div>
        )}
      </div>
    </div>
  );
}
